import React, { useCallback, useEffect, useRef } from "react";
import PostDetailShareIcon from "./PostDetailShareIcon";
import { showPostDetailToast } from "./postDetailToast";

/**
 * Lovable-style share menu for post detail — prod URLs only (copy, X, Facebook).
 */
const PostDetailShareMenu = ({
  url,
  twitterUrl,
  facebookUrl,
  show = false,
  onToggle,
  shareRef,
}) => {
  const internalRef = useRef(null);
  const rootRef = shareRef || internalRef;

  const close = useCallback(() => {
    if (show && onToggle) {
      onToggle();
    }
  }, [show, onToggle]);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        close();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        close();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [show, close]);

  const handleCopy = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      showPostDetailToast("Link copied");
      close();
    } catch {
      // Clipboard failed silently — no toast.
    }
  };

  const handleTriggerClick = (event) => {
    event.stopPropagation();
    onToggle?.();
    if (!show) {
      event.currentTarget.blur();
    }
  };

  return (
    <div ref={rootRef} className="post-detail-share-menu">
      <button
        type="button"
        className="post-detail-share-menu__trigger"
        onClick={handleTriggerClick}
        aria-expanded={show}
        aria-haspopup="menu"
        aria-label="Share post"
      >
        <PostDetailShareIcon />
      </button>

      <div
        className={[
          "post-detail-share-menu__dropdown",
          show && "post-detail-share-menu__dropdown--open",
        ]
          .filter(Boolean)
          .join(" ")}
        role="menu"
        aria-label="Share options"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="post-detail-share-menu__item"
          role="menuitem"
          onClick={handleCopy}
        >
          <span className="material-icons" aria-hidden="true">
            check
          </span>
          Copy link
        </button>

        {twitterUrl && (
          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="post-detail-share-menu__item"
            role="menuitem"
            onClick={close}
          >
            <i className="fab fa-twitter" aria-hidden="true" />
            Share on X
          </a>
        )}

        {facebookUrl && (
          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="post-detail-share-menu__item"
            role="menuitem"
            onClick={close}
          >
            <span className="material-icons" aria-hidden="true">
              facebook
            </span>
            Facebook
          </a>
        )}
      </div>
    </div>
  );
};

export default PostDetailShareMenu;
